<template>
    <card :to="navigateTo" :flat="flat" :tile="flat" height="80" :outlined="outlined" :no-spacing="flat || noSpacing"
          class="py-16 px-16 flex flex-row items-center" :style="!flat ? {borderLeft: `2px solid ${styleColor} !important`} : null">
        <div class="flex w-full" :class="{'items-center': model.model_name !== 'event'}">
            <div class="flex flex-column pt-1 relative">
                <avatar :model="model" :size="model.model_name === 'event' ? 64 : 44" cover>
                    <div>
						<template v-if="model.model_name === 'event'">
							<div v-if="model.status && [eventStatus.Draft, eventStatus.Canceled, eventStatus.Test].includes(model.status.value)"
								 :style="{backgroundColor: model.status.color}"
								 class="absolute bottom-0 left-0 w-full truncate text-white text-5xs leading-tight py-2 px-2 border-box text-center">
								{{ model.status.title }}
							</div>
							<div v-else-if="model.is_private"
								 class="absolute bottom-0 left-0 w-full truncate bg-warning text-white text-5xs leading-tight py-2 px-2 border-box text-center">
								{{ $t('label.private') }}
							</div>
						</template>
					</div>
                </avatar>
                <div v-if="model.model_name === 'user' && model.club && model.club.avatar_thumbnail" class="absolute bottom-0 right-0 mr--4 mb--4">
                    <avatar :model="model.club" :size="20"/>
                </div>
            </div>

            <div class="flex-grow-x-1 overflow-hidden">
                <div class="pl-16 pr-4 py-0 flex flex-column justify-center">

                    <p v-if="toptitle" class="text-2xs font-heading leading-tight mb-1" :style="{color: styleColor}">{{ toptitle }}</p>
                    <sport-type-text v-else :sport-types="model.sportTypes" class="text-2xs leading-tight mb-1" truncate>
                        <meta-text v-if="model.model_name === 'event'" :color="styleColor">{{ model.type.title }}</meta-text>
						<meta-text v-else-if="model.model_name === 'club'" xx-small :color="styleColor">{{ model.parent_club_id ? $t('label.department') : (model.presentation_type_name ?? $t('label.club')) }}</meta-text>
						<meta-text v-else xx-small :color="styleColor">{{ model.presentation_type_name }}</meta-text>
					</sport-type-text>

                    <div class="flex">
                        <p class="font-heading text-sm leading-tight font-bold text-heading-color mb-0 py-1 truncate">{{ title ? title : model.display_name }}</p>

                        <tooltip v-if="model.verified" :text="$t('label.verified')">
                            <Icon :icon="icons.verified" color="success" class="ml-4 mb-2" xx-small/>
                        </tooltip>
                        <div v-else-if="model.model_name === 'event' && ((model.labels && model.labels.length) || model.attendance_mode > 1)" class="flex ml-2">
                            <Icon v-if="model.attendance_mode > 1" :icon="icons.virtual" xx-small color="gray-300" class="ml-4" />

                            <template v-if="model.labels && model.labels.length">
                                <div v-for="(label, key) in model.labels" :key="key" class="ml-4">
                                    <cdn-image :src="label.icon" :height="16" :ratio="719/865" :alt="label.title"/>
                                </div>
                            </template>
                        </div>
                    </div>

                    <p v-if="subtitle" class="font-heading text-2xs leading-tight flex mt-2 mb-0">{{ subtitle }}</p>
                    <p v-else-if="model.model_name === 'event'" class="font-heading text-2xs leading-tight flex mt-2 mb-0 overflow-hidden">
                        <meta-text xx-small class="text-text-color">{{ $date(model.start_date).formatDateRange(model.end_date) }}</meta-text>
                        <meta-text v-if="model.country_code" class="flex overflow-hidden text-text-color" xx-small>
                            <span v-if="model.country_code === 'ww'" class="whitespace-no-wrap">{{ $t('label.worldwide') }}</span>
                            <template v-else-if="model.country_code">
                                <span v-if="model.city" class="truncate">{{ model.city }},&nbsp;</span>
                                <span class="whitespace-no-wrap">{{ model.country_code.toUpperCase() }}</span>
                            </template>
                        </meta-text>
                    </p>
					<p v-else-if="model.model_name === 'club' && model.country_code" class="font-heading text-2xs leading-tight flex mt-2 mb-0 overflow-hidden">
						<meta-text xx-small>
							<span v-if="model.city" class="truncate">{{ model.city }},&nbsp;</span>
							<span class="whitespace-no-wrap">{{ model.country_code.toUpperCase() }}</span>
						</meta-text>
					</p>
					<p v-else-if="model.model_name === 'user' && model.club" class="font-heading text-2xs leading-tight flex mt-2 mb-0 overflow-hidden">
						<meta-text xx-small>{{ model.club.display_name }}</meta-text>
					</p>

                </div>
            </div>

            <div v-if="$slots.actions">
                <lazy-wrapper>
                    <slot name="actions"/>
                </lazy-wrapper>
            </div>
        </div>
    </card>
</template>

<script>
import CardMixin from "../../Card/CardMixin";
import {EventStatus} from "@spoferan/spoferan-ts-core";
import {mdiCellphoneNfc, mdiShieldCheck} from "@mdi/js";

export default {
    name: 'ProfilePreview',

    mixins: [CardMixin],

    props: {

        // The model to show as preview
        // This can one of the following: user, member, event, club
        model: {
            type: Object,
            required: true
        },

        // The title to show instead of the model's name
        title: {
            type: String,
            default: null
        },

        // The subtitle to show above the model name. If not specified, the sport types will be shown at that place for clubs and nothing for users
        toptitle: {
            type: String,
            default: null
        },

        // The subtitle to below above the model name. If not specified, the sport types will be shown at that place for user and the city for clubs and events
        subtitle: {
            type: String,
            default: null
        },

        // Where to redirect the user when he clicks on the profile
        to: {
            type: [String, Boolean, Object],
            default: null
        },

        color: {
            type: String,
            default: null
        },

    },

    data() {
        return {
            eventStatus: EventStatus,
            icons: {
                virtual: mdiCellphoneNfc,
                verified: mdiShieldCheck
            }
        }
    },

    computed: {
        navigateTo() {
            if (this.to === false) {
                return null;
            }
            if (this.to) {
                return this.to;
            }

            let modelRouteName = this.model.model_name;
            let modelParamName = this.model.model_name;
            if (modelRouteName === 'member') {
                // A member's '_key' value equals the related user's '_key' value whereby we link to the user profile
                if (this.model.user_id) {
					modelRouteName = 'athlete';
					modelParamName = 'user';
                } else {
                    return null;
                }
            } else if (modelRouteName === 'user') {
				modelRouteName = 'athlete';
				modelParamName = 'user';
			}

			if (modelRouteName) {
				if (this.$config.public.isSpoferanDomain) {
					return this.$app(`${modelRouteName}s-${modelParamName}`, {params: {[modelParamName]: this.model._key}, state: this.model.cache_buster ? {[`${modelParamName}_ts`]: this.model.cache_buster} : undefined});
				}

				return this.$spoferanUrl(`${modelRouteName}s/${this.model._key}`);
			}

            return null;
        },

        styleColor() {
            if (this.color) {
                return this.color;
            }

            return this.model.color ?? this.$config.public.colors.primary
        }
    }
}
</script>